import * as React from 'react'
import styled from '@emotion/styled/macro'

import { OutlineButton } from '~/components/atoms/OutlineButton'
import Icon from '~/components/atoms/Icon'
import { useClickWithDisabledReason } from '~/hooks/useClickWithDisabledReason'

interface Props {
  isInitialFooter: boolean
  isGenerating: boolean
  hasError: boolean
  isInputEmpty: boolean
  onGenerate: () => void
  onRetry: () => void
  onReset: () => void
  onClose: () => void
  canProceed: boolean
  onProceedToEngraving?: () => void
}

export const AICustomizationSidebarFooter = ({
  isInitialFooter,
  isGenerating,
  hasError,
  isInputEmpty,
  onGenerate,
  onRetry,
  onReset,
  onClose,
  canProceed,
  onProceedToEngraving
}: Props) => {
  const common = useClickWithDisabledReason({
    disabled: isGenerating,
    ariaDisabled: !isGenerating && isInputEmpty,
    reason: isInputEmpty
      ? {
          type: 'validation_error' as const,
          message: 'プロンプトを入力するか、色を1つ以上選んでください。'
        }
      : null,
    onClick: hasError ? onRetry : onGenerate
  })

  const primaryLabel = isGenerating
    ? '生成中...'
    : hasError
    ? '再試行'
    : 'AIでデザインを生成'

  return (
    <Footer>
      <FooterInner>
        {isInitialFooter ? (
          // 初期フッター
          isInputEmpty ? (
            <FooterPrimaryButtonFull
              {...common.buttonProps}
              colorTheme="teal"
              disabled={isGenerating}
              title={primaryLabel}
              aria-label={primaryLabel}
            >
              {primaryLabel}
            </FooterPrimaryButtonFull>
          ) : (
            <>
              <FooterIconButton
                colorTheme="white"
                shape="circle"
                size="small"
                onClick={onReset}
                disabled={isGenerating}
                title="クリア"
                aria-label="クリア"
              >
                <Icon type="trash" role="img" />
              </FooterIconButton>
              <FooterPrimaryButtonSpan3
                {...common.buttonProps}
                colorTheme="teal"
                disabled={isGenerating}
                title={primaryLabel}
                aria-label={primaryLabel}
              >
                {primaryLabel}
              </FooterPrimaryButtonSpan3>
            </>
          )
        ) : (
          // 生成後フッター
          <>
            <FooterIconButton
              colorTheme="white"
              shape="circle"
              size="small"
              onClick={onReset}
              disabled={isGenerating}
              title="クリア"
              aria-label="クリア"
            >
              <Icon type="trash" role="img" />
            </FooterIconButton>

            <FooterIconButton
              {...common.buttonProps}
              colorTheme="white"
              shape="circle"
              size="small"
              title="再生成"
              aria-label="再生成"
            >
              <Icon type="refresh" role="img" />
            </FooterIconButton>

            <FooterPrimaryButton
              colorTheme="teal-outline"
              onClick={onClose}
              disabled={isGenerating}
              title="手動で調整"
              aria-label="手動で調整"
            >
              手動で調整
            </FooterPrimaryButton>

            {canProceed ? (
              <FooterPrimaryButton
                colorTheme="teal"
                onClick={onProceedToEngraving}
                disabled={isGenerating}
                title="デザイン決定"
                aria-label="デザイン決定"
              >
                デザイン決定
              </FooterPrimaryButton>
            ) : null}
          </>
        )}
      </FooterInner>
    </Footer>
  )
}

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  background: var(--white);
  border-top: 1px solid var(--gray-200);
  padding: var(--gutter-medium);
  margin-top: auto;
`

const FooterInner = styled.div`
  display: grid;
  grid-template-columns: max-content max-content 1fr 1fr;
  align-items: center;
  gap: var(--gutter-small);
  padding: 0;
`

const FooterPrimaryButton = styled(OutlineButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  /* 背景・枠色は OutlineButton の colorTheme に従う */
`

const FooterPrimaryButtonFull = styled(FooterPrimaryButton)`
  grid-column: 1 / -1;
`

// 初期フッターでクリア + 生成の2要素にするため、生成ボタンを3カラム分に拡張
const FooterPrimaryButtonSpan3 = styled(FooterPrimaryButton)`
  grid-column: span 3;
`

const FooterIconButton = styled(OutlineButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  & svg {
    width: 18px;
    height: 18px;
  }
`

export default AICustomizationSidebarFooter
